/**
 * TOSS Web Application — Client Portal profile query
 *
 * `GET /api/client/users/me` — the signed-in customer's own account, as rendered by the
 * «Личные данные» block of `/client/profile`.
 *
 * Editable since the roles release: `useUpdateClientProfile` below writes ФИО, phone, должность
 * and the push preference back through `PUT /api/client/users/me`. Email is still not among them
 * and will not be — it is the login, and the portal has no confirmation mail to catch a typo with,
 * so changing it stays a ТОСС operation.
 *
 * Why this is a query at all when `authStore.user` already holds the same fields: the
 * store's copy is a snapshot taken at login (or at `initialize()` on reload) and is never
 * refreshed while a session lives. This is the one screen whose whole purpose is to show
 * those values, so it reads them from the server.
 *
 * Lives in `hooks/` rather than in `features/client/**` like the rest of the portal's
 * data access (decision Р6 makes `features/client/**` unimportable from outside, so
 * anything the layout chrome might ever want has to sit here) — same reasoning as
 * `useClientBadges` and `useClientFiles`.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { getErrorMessage, isAxiosError } from '@/api/client';
import {
  clientCompanyApi,
  type ClientProfileDto,
  type UpdateClientProfileRequest,
} from '@/api/clientPortal/clientCompany.api';
import { clientKeys } from '@/hooks/api/clientPortal/clientQueryKeys';
import {
  applyCompanySwitch,
  discardForeignRouteOnCompanySwitch,
} from '@/hooks/api/clientPortal/clientCompanySwitch';
import { useAuthStore } from '@/store/authStore';
import { UserRole } from '@/types';

// ============================================================================
// Constants
// ============================================================================

/**
 * How long the profile stays fresh. Generous on purpose: nothing in the portal can
 * change these fields, so a refetch is only ever about a change an administrator made
 * on the internal side.
 */
const PROFILE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Poll cadence for an account that has more than one legal entity — the same 60 seconds
 * `useClientBadges` uses for the bell, and for the same reason: since decision B7
 * amendment 1 this payload carries `availableCompanies[].unreadCount`, the portal's only
 * signal that something is waiting under a company the session is NOT acting for.
 *
 * That signal cannot arrive any other way. A notification raised for another of the
 * account's companies is deliberately not pushed over SignalR (`NotificationsService`
 * suppresses the live push outside the active company) and is filtered out of the feed and
 * the badge, so nothing would invalidate this query on its own — the counter would sit at
 * its page-load value for as long as the tab stayed open.
 */
const COMPANY_COUNTER_REFETCH_INTERVAL_MS = 60_000;

// ============================================================================
// Error mapping
// ============================================================================

/** Backend error code out of the error envelope, when there is one. */
function errorCode(error: unknown): string | undefined {
  return isAxiosError(error)
    ? (error.response?.data as { code?: string } | undefined)?.code
    : undefined;
}

/**
 * Turn a failed profile read into a sentence a customer can act on.
 *
 * Only `CLIENT_COMPANY_NOT_BOUND` needs saying in the portal's own words — it is a Р1
 * code and therefore absent from the shared `ERROR_MESSAGES` map, and it is the one
 * failure a customer cannot resolve alone. Everything else falls through to
 * `getErrorMessage()`, which already knows the backend envelope and the offline case.
 */
export function getClientProfileErrorMessage(error: unknown): string {
  if (errorCode(error) === 'CLIENT_COMPANY_NOT_BOUND') {
    return 'Учётная запись не привязана к компании. Обратитесь к вашему менеджеру ТОСС.';
  }

  return getErrorMessage(error);
}

// ============================================================================
// Hook
// ============================================================================

/**
 * The signed-in portal account.
 *
 * Gated on an authenticated **client** session, exactly like the badge counters: an
 * internal user who lands on a portal URL for one render (a stale link, a guard redirect
 * mid-flight) must not fire `/api/client/*` calls that would answer 403.
 *
 * **Polls only for a multi-company account**, and the condition is read off the answer
 * itself rather than off a second source of truth. One company means no switcher, no
 * cross-company counter to keep fresh, and therefore no reason to spend a request a
 * minute — which is essentially every account today, so the default cost of this hook is
 * unchanged from before the counter existed.
 *
 * @example
 * ```tsx
 * const { data: profile, isPending, isError, error, refetch } = useClientProfile();
 * ```
 */
export function useClientProfile(): UseQueryResult<ClientProfileDto, Error> {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isClient = useAuthStore((state) => state.user?.role === UserRole.Client);

  return useQuery<ClientProfileDto, Error>({
    queryKey: clientKeys.profile(),
    queryFn: () => clientCompanyApi.getProfile(),
    enabled: isAuthenticated && isClient,
    staleTime: PROFILE_STALE_TIME_MS,
    refetchInterval: (query) =>
      (query.state.data?.availableCompanies.length ?? 0) > 1
        ? COMPANY_COUNTER_REFETCH_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
  });
}

// ============================================================================
// Mutation — edit own profile
// ============================================================================

/**
 * `PUT /api/client/users/me` — ФИО, phone, должность and the push preference.
 *
 * The response is the whole profile, so it goes straight into the cache: a refetch would show the
 * old values for the length of one request on the very screen that just saved them.
 *
 * `authStore` is updated in the same breath. The header, the sidebar and the profile hub read the
 * store's snapshot rather than this query, and a rename that only landed in the cache would leave
 * the customer's own name stale everywhere except the form they typed it into.
 */
export function useUpdateClientProfile(): UseMutationResult<
  ClientProfileDto,
  Error,
  UpdateClientProfileRequest
> {
  const queryClient = useQueryClient();

  return useMutation<ClientProfileDto, Error, UpdateClientProfileRequest>({
    mutationFn: (data) => clientCompanyApi.updateProfile(data),
    onSuccess: (profile) => {
      queryClient.setQueryData(clientKeys.profile(), profile);

      useAuthStore.getState().updateUser({
        fullName: profile.fullName,
        phoneNumber: profile.phoneNumber ?? undefined,
      });

      // The roster shows the same names, and the colleague reading it is often the person who was
      // just renamed.
      void queryClient.invalidateQueries({ queryKey: clientKeys.colleagues() });
      void queryClient.invalidateQueries({ queryKey: clientKeys.employees.all });
    },
  });
}

// ============================================================================
// Mutation — switch active company
// ============================================================================

/**
 * Switch which of the caller's own companies the session acts for
 * (`POST /api/client/users/active-company`, release Р4, decision Р20).
 *
 * The only consumer today is {@link ClientCompanySwitcher}, but the whole "what a switch
 * changes" sequence lives in `applyCompanySwitch` rather than here, because
 * `useClientSessionRealtime` needs the identical sequence for the `CompanySwitched`
 * SignalR push (a switch forced from another tab, or by an administrator revoking the
 * account's active company) — see that module for why.
 *
 * 404 (the company was named in a stale dropdown — an admin revoked the membership
 * between render and click) refetches the profile so the option disappears from
 * `availableCompanies` the next time the dropdown opens; a network failure needs no
 * handling here at all, since nothing was written and the previous company and the cache
 * are already exactly as they should be. Both are surfaced to the caller via the returned
 * mutation's own `isError` / `error`, per screens §6's own two error states.
 *
 * @example
 * ```tsx
 * const switchCompany = useSwitchCompany();
 * switchCompany.mutate(company.id, { onError: (err) => showMyOwnCopy(err) });
 * ```
 */
export function useSwitchCompany(): UseMutationResult<ClientProfileDto, Error, string> {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  return useMutation<ClientProfileDto, Error, string>({
    mutationFn: (customerId: string) => clientCompanyApi.switchActiveCompany(customerId),
    onSuccess: (profile) => {
      // Navigate FIRST, synchronously, THEN apply the cache/session change — never the
      // other order. See `discardForeignRouteOnCompanySwitch`'s own doc: swapping them
      // lets a still-open detail page's own query race-answer 404 under the new company
      // before this redirects it away, which is exactly the "customer briefly sees a 404"
      // outcome the company-switch contract's §6 calls out as the wrong answer.
      discardForeignRouteOnCompanySwitch(navigate, location.pathname, location.search);
      void applyCompanySwitch({ queryClient, profile });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 404) {
        void queryClient.invalidateQueries({ queryKey: clientKeys.profile() });
      }
    },
  });
}
